import { useEffect, useState } from 'react';
import { Layout } from '../components/layout/Layout';
import { Header } from '../components/layout/Header';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../components/ui/alert-dialog';
import { getUsers, deleteUser, updateUserRole, getAzureDirectoryUsers, importAzureUser } from '../lib/api';
import { toast } from 'sonner';
import { Users, Download, Trash2, Shield, Eye, Loader2, Check, UserPlus } from 'lucide-react';

export const UsersPage = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [importOpen, setImportOpen] = useState(false);
  const [azureUsers, setAzureUsers] = useState([]);
  const [loadingAzure, setLoadingAzure] = useState(false);
  const [importing, setImporting] = useState({});

  const fetchUsers = async () => {
    try {
      const response = await getUsers();
      setUsers(response.data);
    } catch (error) {
      toast.error('Erreur lors du chargement des utilisateurs');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const handleOpenImport = async () => {
    setImportOpen(true);
    setLoadingAzure(true);
    try {
      const response = await getAzureDirectoryUsers();
      setAzureUsers(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la récupération des utilisateurs Azure AD');
    } finally {
      setLoadingAzure(false);
    }
  };

  const handleImport = async (azureUser) => {
    setImporting(prev => ({ ...prev, [azureUser.email]: true }));
    try {
      await importAzureUser({
        name: azureUser.name,
        email: azureUser.email,
        role: 'viewer',
        azure_oid: azureUser.azure_oid,
      });
      toast.success(`${azureUser.name} importé avec succès`);
      // Refresh both lists
      setAzureUsers(prev => prev.map(u =>
        u.email === azureUser.email ? { ...u, is_imported: true, role: 'viewer' } : u
      ));
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'import');
    } finally {
      setImporting(prev => ({ ...prev, [azureUser.email]: false }));
    }
  };

  const handleDelete = async (id) => {
    try {
      await deleteUser(id);
      toast.success('Utilisateur supprimé');
      fetchUsers();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleRoleChange = async (id, newRole) => {
    try {
      await updateUserRole(id, newRole);
      toast.success('Rôle mis à jour');
      fetchUsers();
    } catch (error) {
      toast.error('Erreur lors de la mise à jour du rôle');
    }
  };

  return (
    <Layout>
      <Header
        title="Gestion des utilisateurs"
        subtitle="Gérez les accès à l'application Cost Cloud"
      />
      <div className="flex justify-end mb-6">
        <Button onClick={handleOpenImport} data-testid="import-users-btn">
          <Download className="w-4 h-4 mr-2" />
          <span className="hidden sm:inline">Importer utilisateurs SSO</span>
          <span className="sm:hidden">Importer SSO</span>
        </Button>
      </div>

      {/* Import Dialog */}
      <Dialog open={importOpen} onOpenChange={setImportOpen}>
        <DialogContent className="max-w-[95vw] sm:max-w-2xl max-h-[80vh] flex flex-col">
          <DialogHeader>
            <DialogTitle>Importer des utilisateurs Azure AD</DialogTitle>
            <DialogDescription>
              Sélectionnez les utilisateurs de votre annuaire Azure AD à importer dans Cost Cloud.
            </DialogDescription>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto mt-4">
            {loadingAzure ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
                <span className="ml-2 text-muted-foreground">Chargement de l'annuaire Azure AD...</span>
              </div>
            ) : azureUsers.length === 0 ? (
              <div className="text-center py-12">
                <Users className="w-10 h-10 mx-auto text-muted-foreground mb-3" />
                <p className="text-muted-foreground">Aucun utilisateur trouvé dans l'annuaire.</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Nom</TableHead>
                    <TableHead className="hidden sm:table-cell">Email</TableHead>
                    <TableHead className="text-right">Action</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {azureUsers.map(au => (
                    <TableRow key={au.email}>
                      <TableCell className="font-medium">
                        {au.name}
                        <span className="block sm:hidden text-xs text-muted-foreground font-normal truncate max-w-[180px]">{au.email}</span>
                      </TableCell>
                      <TableCell className="hidden sm:table-cell text-muted-foreground">{au.email}</TableCell>
                      <TableCell className="text-right">
                        {au.is_imported ? (
                          <Badge variant="secondary" className="gap-1">
                            <Check className="w-3 h-3" />
                            {au.role === 'admin' ? 'Admin' : 'Lecteur'}
                          </Badge>
                        ) : (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleImport(au)}
                            disabled={importing[au.email]}
                          >
                            {importing[au.email] ? (
                              <Loader2 className="w-3 h-3 animate-spin mr-1" />
                            ) : (
                              <UserPlus className="w-3 h-3 mr-1" />
                            )}
                            Importer
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Users Table */}
      <Card data-testid="users-table-card">
        <CardHeader>
          <CardTitle className="text-lg font-[Outfit]">Utilisateurs</CardTitle>
          <CardDescription>
            {users.length} utilisateur(s) enregistré(s) — Lecteur : Dashboard, Services | Admin : tous les menus
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="h-48 flex items-center justify-center">
              <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
            </div>
          ) : users.length === 0 ? (
            <div className="text-center py-12">
              <Users className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
              <p className="text-muted-foreground">Aucun utilisateur importé</p>
              <p className="text-sm text-muted-foreground mt-2">
                Importez des utilisateurs depuis votre annuaire Azure AD.
              </p>
            </div>
          ) : (
            <>
              {/* Desktop table */}
              <div className="hidden sm:block">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Nom</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>Rôle</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {users.map(user => (
                      <TableRow key={user.id} data-testid={`user-row-${user.id}`}>
                        <TableCell className="font-medium">{user.name}</TableCell>
                        <TableCell>{user.email}</TableCell>
                        <TableCell>
                          <Select
                            value={user.role}
                            onValueChange={(value) => handleRoleChange(user.id, value)}
                          >
                            <SelectTrigger className="w-36" data-testid={`role-select-${user.id}`}>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="viewer">
                                <div className="flex items-center gap-2">
                                  <Eye className="w-3 h-3" />
                                  Lecteur
                                </div>
                              </SelectItem>
                              <SelectItem value="admin">
                                <div className="flex items-center gap-2">
                                  <Shield className="w-3 h-3" />
                                  Admin
                                </div>
                              </SelectItem>
                            </SelectContent>
                          </Select>
                        </TableCell>
                        <TableCell className="text-right">
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="text-destructive hover:text-destructive"
                                data-testid={`delete-user-${user.id}`}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>Supprimer l'utilisateur ?</AlertDialogTitle>
                                <AlertDialogDescription>
                                  Cette action est irréversible. L'utilisateur {user.name} ne pourra plus accéder à l'application.
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>Annuler</AlertDialogCancel>
                                <AlertDialogAction
                                  onClick={() => handleDelete(user.id)}
                                  className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                                >
                                  Supprimer
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              {/* Mobile cards */}
              <div className="sm:hidden space-y-3">
                {users.map(user => (
                  <div
                    key={user.id}
                    data-testid={`user-row-${user.id}`}
                    className="border border-border rounded-lg p-4 space-y-3"
                  >
                    <div className="flex items-start justify-between">
                      <div className="min-w-0 flex-1">
                        <p className="font-medium truncate">{user.name}</p>
                        <p className="text-sm text-muted-foreground truncate">{user.email}</p>
                      </div>
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="text-destructive hover:text-destructive flex-shrink-0 -mt-1 -mr-2"
                            data-testid={`delete-user-${user.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Supprimer l'utilisateur ?</AlertDialogTitle>
                            <AlertDialogDescription>
                              Cette action est irréversible. L'utilisateur {user.name} ne pourra plus accéder à l'application.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Annuler</AlertDialogCancel>
                            <AlertDialogAction
                              onClick={() => handleDelete(user.id)}
                              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                            >
                              Supprimer
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                    <Select
                      value={user.role}
                      onValueChange={(value) => handleRoleChange(user.id, value)}
                    >
                      <SelectTrigger className="w-full" data-testid={`role-select-${user.id}`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="viewer">
                          <div className="flex items-center gap-2">
                            <Eye className="w-3 h-3" />
                            Lecteur
                          </div>
                        </SelectItem>
                        <SelectItem value="admin">
                          <div className="flex items-center gap-2">
                            <Shield className="w-3 h-3" />
                            Admin
                          </div>
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                ))}
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </Layout>
  );
};
